import { useState } from "react";
import { SequenceTemplate } from "@/components/sequences/types";
import { EditSequenceDialog } from "@/components/sequences/EditSequenceDialog";
import { SavedSequencesList } from "@/components/sequences/SavedSequencesList";
import { useToast } from "@/hooks/use-toast";
import { useSequences } from "@/components/sequences/hooks/useSequences";
import { useSequenceOperations } from "@/components/sequences/hooks/useSequenceOperations";
import { supabase } from "@/integrations/supabase/client";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export const SavedSequencesTab = () => {
  const [editingSequence, setEditingSequence] = useState<SequenceTemplate | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sequenceToDelete, setSequenceToDelete] = useState<string | null>(null);
  const { toast } = useToast();
  const { data: activeSequences, refetch: refetchSequences } = useSequences();
  const { handleDelete } = useSequenceOperations();

  const onDelete = async (id: string) => {
    setSequenceToDelete(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!sequenceToDelete) return;

    try {
      await handleDelete(sequenceToDelete);
      await refetchSequences();
      toast({
        title: "Success",
        description: "Sequence deleted successfully",
      });
    } catch (error) {
      console.error("Delete operation failed:", error);
      toast({
        title: "Error",
        description: "Failed to delete sequence. Please try again.",
        variant: "destructive",
      });
    } finally {
      setDeleteDialogOpen(false);
      setSequenceToDelete(null);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold tracking-tight">Saved Sequences</h2>
        <p className="text-muted-foreground">View and manage your saved sequences</p>
      </div>

      <SavedSequencesList 
        sequences={activeSequences || []}
        onEdit={(sequence) => {
          setEditingSequence(sequence);
          setEditDialogOpen(true);
        }}
        onDelete={onDelete}
      />

      <EditSequenceDialog
        sequence={editingSequence}
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        onSave={async (sequence, steps) => {
          try {
            // Update sequence template
            const { error: templateError } = await supabase
              .from('sequence_templates')
              .update({
                name: sequence.name,
                description: sequence.description,
              })
              .eq('id', sequence.id);

            if (templateError) throw templateError;

            // Delete existing steps
            const { error: deleteError } = await supabase
              .from('sequence_steps')
              .delete()
              .eq('sequence_id', sequence.id);

            if (deleteError) throw deleteError;

            // Insert new steps
            const { error: stepsError } = await supabase
              .from('sequence_steps')
              .insert(steps.map(step => ({
                sequence_id: sequence.id,
                step_number: step.step_number,
                channel: step.channel,
                content_template: step.content_template,
                delay_hours: step.delay_hours,
                conditions: { auto_trigger: step.auto_trigger }
              })));

            if (stepsError) throw stepsError;

            await refetchSequences();
            toast({
              title: "Success",
              description: "Sequence updated successfully",
            });
          } catch (error) {
            console.error("Edit error:", error);
            toast({
              title: "Error",
              description: "Failed to update sequence. Please try again.",
              variant: "destructive",
            });
            throw error;
          }
        }}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the sequence
              and all its associated data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};